import { useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { tasksService } from '../api/tasks.service';
import { adaptTaskSummaryDto } from '../adapters';
import { serializeFilters } from '../utils/filter-serializer';
import { TASK_KEYS } from './queryKeys';
import { TASK_COLLECTION_STALE_TIME } from './taskQueryConfig';
import type { TaskFilters } from '../types';
import type { Task } from '../types';

export interface CalendarDateRange {
  start: string;
  end: string;
}

export interface UseTaskCalendarTasksResult {
  data: Task[] | undefined;
  isLoading: boolean;
  isError: boolean;
  isTruncated: boolean;
}

const CALENDAR_PAGE_SIZE = 100;

/**
 * Fetches tasks for the calendar view, filtered to the visible date window.
 *
 * Uses the filter DSL (`dueDate` with `gte`/`lte`) to constrain results to the
 * visible calendar range. If the user has already set a `dueDate` filter, their
 * filter takes precedence and the window constraint is skipped.
 *
 * @param projectId - The project to load tasks for.
 * @param boardId - The board to filter by.
 * @param filters - Active UI filter state.
 * @param range - Visible calendar window as `YYYY-MM-DD` strings.
 */
export function useTaskCalendarTasks(
  projectId: string,
  boardId: string,
  filters: TaskFilters,
  range: CalendarDateRange,
): UseTaskCalendarTasksResult {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  // Build combined filters: inject date window only when the user hasn't set one already.
  const userHasDueDateFilter =
    !!filters.dueDateFrom ||
    !!filters.dueDateTo ||
    filters.operatorsByField?.dueDate === 'isEmpty' ||
    filters.operatorsByField?.dueDate === 'isNotEmpty';

  const combinedFilters: TaskFilters = userHasDueDateFilter
    ? filters
    : { ...filters, dueDateFrom: range.start, dueDateTo: range.end };

  const serializedFilters = serializeFilters(combinedFilters);

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      ...TASK_KEYS.calendarQuery(orgId ?? '', projectId, boardId),
      serializedFilters,
      filters.search,
      range.start,
      range.end,
    ],
    queryFn: async ({ signal }) => {
      const result = await tasksService.getQuery(
        projectId,
        {
          view: 'list',
          limit: CALENDAR_PAGE_SIZE,
          boardId,
          ...(serializedFilters && { filters: serializedFilters }),
          ...(filters.search && { search: filters.search }),
        },
        signal,
      );
      return {
        items: (result.items ?? []).map(adaptTaskSummaryDto),
        total: result.total,
      };
    },
    staleTime: TASK_COLLECTION_STALE_TIME,
    enabled: !!orgId && !!projectId && !!boardId,
  });

  return {
    data: data?.items,
    isLoading,
    isError,
    isTruncated: data !== undefined && data.total > CALENDAR_PAGE_SIZE,
  };
}
